import { InterfaceFactoryOptions } from './factory-options.interface';
import { ShapeLinkFlagComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/shape-link-flag-component';
import { ElementBaseComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/base-component/element-base-component';
import { ForegroundComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/foreground-component';
import { PlayerIconComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/player-icon-component';
import { Container } from '@tencent/alloy-renderer';
import { CANVAS_IMAGE_ID_PREFIX } from '@src/slide/content-viewer/service/canvas-renderer/core/id-constants';
import RendererComponent3D from '../components/3d';
import { shouldRender3D } from './utils/toggle-3d';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { ignoreRendererFiltersFor3dDisplayObject } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/utils';
import {
    GifForegroundComponent,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/gif-foreground-component';
import { ImageTypes } from '@src/slide/infrastructure/module/dependent/image/constant/image-type';
import { isDomGifPictureModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { RenderPostionType } from '@src/js/constant';

/**
 * 图片模块
 * 包含：
 * 底板（基础graphics）：图片下方的部分，背景、边框等
 * 前景（前景graphics）：实际图片
 */
export class RendererImageComponent extends Container {
    // 图形构造入参
    public factoryOptions: InterfaceFactoryOptions<IPictureModel>;
    // 链接标识小图标（右键编辑链接生成）
    public shapeLinkFlagComponent: ShapeLinkFlagComponent;
    // 图片前景graphics
    public foregroundComponent: ForegroundComponent | null = null;
    // 图片背景graphics
    public baseComponent: ElementBaseComponent;
    // 视频占位图上的播放 Icon
    private playerIconComponent: PlayerIconComponent;
    // 带 3d 属性的图片渲染组件
    private rendererComponent3D?: RendererComponent3D;
    private isGif = false;

    public constructor(factoryOptions: InterfaceFactoryOptions<IPictureModel>) {
        super();
        this.factoryOptions = factoryOptions;
        const { model } = this.factoryOptions;
        this.id = `${CANVAS_IMAGE_ID_PREFIX}-${model.key}`;

        // 图形渲染
        this.renderAllGraphic();
    }

    public dispose(): void {
        super.dispose();
        this.foregroundComponent?.dispose();
        this.logic?.dispose?.();
    }

    public updateComponent = (): void => {
        this.renderAllGraphic();
    };

    public renderGifForeground() {
        const { model: { hidden } } = this.factoryOptions;
        if (hidden && this.foregroundComponent) {
            this.foregroundComponent?.dispose();
            this.removeChild(this.foregroundComponent);
            this.foregroundComponent = null;
            return;
        }

        if (!this.isGif && this.foregroundComponent) {
            this.foregroundComponent?.dispose();
            this.removeChild(this.foregroundComponent);
            this.foregroundComponent = null;
        }

        if (!this.foregroundComponent) {
            this.foregroundComponent = new GifForegroundComponent(this.factoryOptions);
            this.addChild(this.foregroundComponent);
        }
        this.foregroundComponent.renderComponent();
        this.isGif = true;
    }

    // 插入|更新 图片前景
    // eslint-disable-next-line complexity
    public renderForeground() {
        const useGif = ((this.factoryOptions.model as IPictureModel)?.props?.imageType === ImageTypes.GIF)
            && this.factoryOptions.gifService
            && !this.factoryOptions.isCopyToImage
            && (this.factoryOptions.region === RenderPostionType.CANVAS
                || this.factoryOptions.region === RenderPostionType.PLAYER)
            && !isDomGifPictureModel(this.factoryOptions.model)
        ;
        if (useGif) {
            this.renderGifForeground();
            return;
        }

        if (this.isGif && this.foregroundComponent) {
            this.foregroundComponent?.dispose();
            this.removeChild(this.foregroundComponent);
            this.foregroundComponent = null;
        }

        if (!this.foregroundComponent) {
            this.foregroundComponent = new ForegroundComponent(this.factoryOptions);
            this.addChild(this.foregroundComponent);
        }
        this.foregroundComponent.renderComponent();
        if (this.checkShouldRender3D()) {
            this.foregroundComponent.foregroundGraphics.promiseImageLoaded().then(() => {
                this.rendererComponent3D?.render3D();
            });
        }
        this.isGif = false;
    }

    private get logic(): any {
        return this.factoryOptions.logicService.getLogic(this.factoryOptions);
    }

    // 所有图形模块渲染
    private renderAllGraphic = () => {
        // 插入图片底板
        this.renderBase();
        // 渲染图片前景
        this.renderForeground();
        if (this.checkShouldRender3D()) {
            // 3D 渲染
            this.render3D();
        }
        // // 插入链接标识小图标（右键编辑链接生成）
        this.renderShapeLinkFlag();
        // // 如果是视频，占位图片中心添加播放 Icon
        this.renderPlayerIcon();
    };

    // 插入|更新 图片底板
    private renderBase = () => {
        if (!this.baseComponent) {
            this.baseComponent = new ElementBaseComponent(this.factoryOptions);
            this.addChild(this.baseComponent);
        }
        this.baseComponent.renderComponent();
    };

    private render3D = () => {
        if (RendererComponent3D.isValid3DModel(this.factoryOptions.model)) {
            if (!this.rendererComponent3D) {
                this.rendererComponent3D = new RendererComponent3D(this.factoryOptions, (textureCanvas) => {
                    ignoreRendererFiltersFor3dDisplayObject(this.baseComponent?.children[0]);
                    ignoreRendererFiltersFor3dDisplayObject(this.foregroundComponent?.children[0]);
                    this.baseComponent?.render(textureCanvas);
                    this.foregroundComponent?.render(textureCanvas);
                });
            }
            this.rendererComponent3D.render3D();
            if (!this.children.includes(this.rendererComponent3D)) {
                [...this.children].forEach((component) => {
                    component.dispose();
                    this.removeChild(component);
                });
                // this.clearProps();
                this.addChild(this.rendererComponent3D);
            }
        } else if (this.rendererComponent3D && this.children.includes(this.rendererComponent3D)) {
            this.rendererComponent3D.dispose();
            this.removeChild(this.rendererComponent3D);
            this.rendererComponent3D = undefined;
            this.addChild(this.baseComponent);
            this.foregroundComponent && this.addChild(this.foregroundComponent);
        }
    };

    // 插入|更新 链接标识小图标（右键编辑链接生成）
    private renderShapeLinkFlag = () => {
        if (!this.shapeLinkFlagComponent) {
            this.shapeLinkFlagComponent = new ShapeLinkFlagComponent(this.factoryOptions, this);
        }
        this.shapeLinkFlagComponent.renderComponent();
    };

    private renderPlayerIcon = () => {
        if (!this.playerIconComponent) {
            this.playerIconComponent = new PlayerIconComponent(this.factoryOptions, this);
        }
        this.playerIconComponent.updateComponent();
    };

    private checkShouldRender3D(): boolean {
        return shouldRender3D()
            && !this.factoryOptions.fromScale; // todo: 临时解决加载千页ppt内存过大问题：从缩略图进入时，不渲染3D
    }
}
